"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import MarqueeRow from "./MarqueeRow";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const STAT_KEYS = [
  "destinations",
  "airlines",
  "passengers",
  "connectivity",
] as const;

/** Por debajo de esto contar no se percibe como conteo (p. ej. «#1») */
const MIN_COUNTABLE = 5;

/**
 * Cifra que sube desde cero al entrar en pantalla.
 * El valor viene de i18n como texto («+90», «11.5M+», «#1»), así que se separa
 * en prefijo, número y sufijo para animar sólo la parte numérica.
 */
function StatValue({ value }: { value: string }) {
  const match = value.match(/^(\D*)([\d.]+)(.*)$/);
  const target = match ? parseFloat(match[2]) : NaN;
  const decimals = match && match[2].includes(".") ? 1 : 0;
  const countable = Boolean(match) && !isNaN(target) && target >= MIN_COUNTABLE;

  const [display, setDisplay] = useState(() =>
    countable ? `${match![1]}${(0).toFixed(decimals)}${match![3]}` : value,
  );
  const ref = useRef<HTMLSpanElement>(null);

  useGSAP(
    () => {
      if (!countable) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setDisplay(value);
        return;
      }

      const counter = { n: 0 };
      const tween = gsap.to(counter, {
        n: target,
        duration: 1.4,
        ease: "power2.out",
        scrollTrigger: { trigger: ref.current, start: "top 92%", once: true },
        onUpdate: () =>
          setDisplay(`${match![1]}${counter.n.toFixed(decimals)}${match![3]}`),
      });

      return () => {
        tween.scrollTrigger?.kill();
        tween.kill();
      };
    },
    { scope: ref, dependencies: [value] },
  );

  return (
    <span
      ref={ref}
      // La cifra se queda en dorado: es el único punto de referencia que
      // destaca sobre el resto del texto, ahora todo en celeste.
      className="text-2xl md:text-4xl font-bold tracking-tight text-[var(--botonesSecundarios-iconoLlamativo)] tabular-nums whitespace-nowrap"
    >
      {display}
    </span>
  );
}

/** Banda de cifras: va entre Destacados y Qué hacer. */
export default function StatsMarquee() {
  const t = useTranslations("homeStats");

  return (
    <section
      aria-label={t("eyebrow")}
      className="w-full bg-[var(--fondoGeneral-secciones)] border-t-2 border-[var(--botonesSecundarios-iconoLlamativo)] py-10 md:py-14 overflow-hidden"
    >
      <MarqueeRow direction="left" speed={60}>
        {STAT_KEYS.map((key) => {
          const delta = t(`${key}.delta`);
          const detail = t(`${key}.detail`);

          return (
            <div
              key={key}
              // items-center y no items-baseline: alineadas por la base, la
              // cifra quedaba arriba y su etiqueta caía por debajo.
              className="flex items-center gap-3 md:gap-4 px-6 md:px-12 border-l border-white/10"
            >
              <StatValue value={t(`${key}.value`)} />
              {/* En móvil la cifra y su etiqueta deben caber juntas en pantalla:
                  si la unidad excede el viewport, nunca se leen a la vez. */}
              <span className="flex flex-col gap-0.5 max-w-[14rem] md:max-w-[22rem]">
                {/* Todo en celeste; la jerarquía la marca la opacidad, no el color */}
                <span className="text-[13px] md:text-base text-[var(--acento-nav)]/90 leading-snug">
                  {t(`${key}.label`)}
                </span>
                {detail && (
                  <span className="text-xs text-[var(--acento-nav)]/65 leading-snug">
                    {detail}
                  </span>
                )}
                {delta && (
                  <span className="text-xs font-semibold text-[var(--acento-nav)]/65 whitespace-nowrap">
                    {delta}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </MarqueeRow>
    </section>
  );
}
